/**
 * Página de partidos asignados para entrenadores
 * 
 * Muestra:
 * - Lista de partidos asignados al entrenador
 * - Filtros por estado (pendientes, en curso, finalizados)
 * - Acciones para cada partido (evaluar, ver detalles)
 */

import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const CoachPartidos = () => {
  const { coachData: _coachData } = useOutletContext();
  const navigate = useNavigate();
  const [partidos, setPartidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos'); // todos, pendientes, finalizados
  const [error, setError] = useState(null);
  const [logrosPartidos, setLogrosPartidos] = useState({}); // Logros por partido
  const [enviandoNotificacion, setEnviandoNotificacion] = useState({}); // Estado de envío por partido
  const [partidosNotificados, setPartidosNotificados] = useState({}); // Partidos ya notificados

  // Variable responsive
  const isMobile = window.innerWidth <= 768;

  // Scroll al top cuando se carga la página
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Cargar partidos del entrenador
  useEffect(() => {
    const fetchPartidos = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('prokids_token');

        const response = await fetch('/api/coach/partidos', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setPartidos(result.data || []);
          } else {
            setError('Error al cargar partidos');
          }
        } else {
          setError(`Error del servidor: ${response.status}`);
          console.error('❌ Error al cargar partidos:', response.status);
        }
      } catch (error) {
        setError('Error de conexión al cargar partidos');
        console.error('Error al cargar partidos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPartidos();
  }, []);

  // Cargar logros y estado de notificación para partidos finalizados
  useEffect(() => {
    const cargarLogrosPartidosFinalizados = async () => {
      const partidosFinalizados = partidos.filter(p => p.estado === 'finalizado');

      for (const partido of partidosFinalizados) {
        const token = localStorage.getItem('prokids_token');

        // Cargar logros si no están cargados
        if (!logrosPartidos[partido.id]) {
          try {
            const response = await fetch(`/api/coach/partidos/${partido.id}/logros`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const result = await response.json();
              if (result.success) {
                setLogrosPartidos(prev => ({
                  ...prev,
                  [partido.id]: result.logros || {}
                }));
              }
            }
          } catch (error) {
            console.error(`Error al cargar logros del partido ${partido.id}:`, error);
          }
        }

        // Verificar si el partido ya fue notificado
        if (partidosNotificados[partido.id] === undefined) {
          try {
            const response = await fetch(`/api/coach/partidos/${partido.id}/notificacion-status`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const result = await response.json();
              if (result.success) {
                setPartidosNotificados(prev => ({
                  ...prev,
                  [partido.id]: result.data.notificado
                }));
              }
            }
          } catch (error) {
            console.error(`Error al verificar notificación del partido ${partido.id}:`, error);
          }
        }
      }
    };

    if (partidos.length > 0) {
      cargarLogrosPartidosFinalizados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partidos]);

  // Filtrar partidos
  const partidosFiltrados = partidos.filter(partido => {
    if (filtro === 'todos') return true;
    if (filtro === 'pendientes') return ['preorganizado', 'confirmado'].includes(partido.estado);
    if (filtro === 'finalizados') return partido.estado === 'finalizado';
    return true;
  });

  // Función para formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return 'Fecha no disponible';
    const fechaObj = new Date(fecha);
    return fechaObj.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Función para formatear hora
  const formatearHora = (hora) => {
    if (!hora) return 'Hora no disponible';
    return hora.substring(0, 5); // HH:MM
  };

  // Función para obtener color del estado
  const getEstadoColor = (estado) => {
    const colores = {
      'preorganizado': '#ffc107',
      'confirmado': '#28a745',
      'finalizado': '#6c757d',
      'suspendido': '#dc3545'
    };
    return colores[estado] || '#6c757d';
  };

  // Función para obtener estilo de badge de logro
  const getBadgeStyle = (logro) => {
    const baseStyle = {
      display: 'inline-block',
      padding: '2px 6px',
      borderRadius: '8px',
      fontSize: '11px',
      fontWeight: 'bold',
      margin: '1px',
      color: 'white'
    };

    switch (logro) {
      case 'asistencia':
        return { ...baseStyle, backgroundColor: '#17a2b8' };
      case 'victoria':
        return { ...baseStyle, backgroundColor: '#28a745' };
      case 'destacado':
        return { ...baseStyle, backgroundColor: '#ffc107', color: '#212529' };
      case 'El Jugón':
        return { ...baseStyle, backgroundColor: '#dc3545' };
      case 'El Cerrojo':
        return { ...baseStyle, backgroundColor: '#6f42c1' };
      case 'El Desequilibrante':
        return { ...baseStyle, backgroundColor: '#fd7e14' };
      case 'El Líder':
        return { ...baseStyle, backgroundColor: '#20c997' };
      case 'El Mejor Compañero':
        return { ...baseStyle, backgroundColor: '#e83e8c' };
      default:
        return { ...baseStyle, backgroundColor: '#6c757d' };
    }
  };

  // Función para obtener resumen de logros de un partido
  const getResumenLogros = (partidoId) => {
    const logros = logrosPartidos[partidoId];
    if (!logros) return null;

    const totalJugadores = Object.keys(logros).length;
    const totalLogros = Object.values(logros).reduce((total, jugadorLogros) =>
      total + jugadorLogros.length, 0
    );
    const totalPuntos = Object.values(logros).reduce((total, jugadorLogros) =>
      total + jugadorLogros.reduce((puntos, logro) => puntos + (logro.puntos * logro.cantidad), 0), 0
    );

    return { totalJugadores, totalLogros, totalPuntos };
  };

  // Función para verificar si un partido tiene logros
  const tieneLogros = (partidoId) => {
    const logros = logrosPartidos[partidoId];
    return logros && Object.keys(logros).length > 0;
  };

  // Función para notificar entrega de ProPoints
  const handleNotificarProPoints = async (partidoId) => {
    try {
      setEnviandoNotificacion(prev => ({ ...prev, [partidoId]: true }));
      const token = localStorage.getItem('prokids_token');

      const response = await fetch(`/api/coach/partidos/${partidoId}/notificar-propoints`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const data = result.data;

        // Mensaje principal
        toast.success('✅ Notificaciones enviadas exitosamente a tutores y futbolistas');

        // Mostrar estadísticas
        if (data.notificaciones_inapp > 0) {
          toast.info(`📱 ${data.notificaciones_inapp} notificaciones in-app enviadas`);
        }

        if (data.emails_enviados > 0) {
          toast.info(`📧 ${data.emails_enviados} emails enviados`);
        }

        // Marcar el partido como notificado
        setPartidosNotificados(prev => ({
          ...prev,
          [partidoId]: true
        }));

      } else {
        toast.error(result.message || 'Error al enviar notificaciones');
      }
    } catch (error) {
      console.error('Error al notificar ProPoints:', error);
      toast.error('Error de conexión al enviar notificaciones');
    } finally {
      setEnviandoNotificacion(prev => ({ ...prev, [partidoId]: false }));
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <div style={{ textAlign: 'center', color: '#4352A9' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⚽</div>
          <p>Cargando partidos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: isMobile ? '40px 20px' : '60px 40px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e1e5e9'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '16px' }}>❌</div>
        <h3 style={{
          fontSize: isMobile ? '1.2rem' : '1.4rem',
          fontWeight: '600',
          color: '#dc3545',
          margin: '0 0 8px 0'
        }}>
          Error al cargar partidos
        </h3>
        <p style={{
          fontSize: isMobile ? '14px' : '15px',
          color: '#6c757d',
          margin: '0 0 24px 0'
        }}>
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: '#4352A9',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: isMobile ? '12px 24px' : '14px 28px',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '20px 16px' : '40px 24px',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      
      {/* Header */}
      <div style={{
        marginBottom: isMobile ? '24px' : '32px'
      }}>
        <h1 style={{
          fontSize: isMobile ? '1.8rem' : '2.5rem',
          fontWeight: '700',
          color: '#061156',
          margin: '0 0 8px 0'
        }}>
          ⚽ Mis Partidos
        </h1>
        <p style={{
          fontSize: isMobile ? '14px' : '16px',
          color: '#6c757d',
          margin: 0
        }}>
          Gestiona los partidos asignados a ti como entrenador
        </p>
      </div>

      {/* Filtros */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: isMobile ? '16px' : '20px',
        marginBottom: isMobile ? '20px' : '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e1e5e9'
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: isMobile ? '8px' : '12px',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: '600',
            color: '#061156',
            marginRight: isMobile ? '8px' : '12px'
          }}>
            Filtrar:
          </span>
          
          {['todos', 'pendientes', 'finalizados'].map(opcion => (
            <button
              key={opcion}
              onClick={() => setFiltro(opcion)}
              style={{
                padding: isMobile ? '6px 12px' : '8px 16px',
                borderRadius: '6px',
                border: '1px solid #e1e5e9',
                backgroundColor: filtro === opcion ? '#4352A9' : '#ffffff',
                color: filtro === opcion ? '#ffffff' : '#495057',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {opcion.charAt(0).toUpperCase() + opcion.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de partidos */}
      {partidosFiltrados.length === 0 ? (
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: isMobile ? '40px 20px' : '60px 40px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e1e5e9'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⚽</div>
          <h3 style={{
            fontSize: isMobile ? '1.2rem' : '1.4rem',
            fontWeight: '600',
            color: '#061156',
            margin: '0 0 8px 0'
          }}>
            No hay partidos {filtro !== 'todos' ? filtro : ''}
          </h3>
          <p style={{
            fontSize: isMobile ? '14px' : '15px',
            color: '#6c757d',
            margin: 0
          }}>
            {filtro === 'todos' 
              ? 'Aún no tienes partidos asignados'
              : `No tienes partidos ${filtro} en este momento`
            }
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: isMobile ? '16px' : '20px'
        }}>
          {partidosFiltrados.map(partido => (
            <div
              key={partido.id}
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: isMobile ? '20px' : '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e1e5e9'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '16px'
              }}>
                <div>
                  <h3 style={{
                    fontSize: isMobile ? '1.1rem' : '1.3rem',
                    fontWeight: '600',
                    color: '#061156',
                    margin: '0 0 8px 0'
                  }}>
                    Partido #{partido.id} - {partido.modalidadTexto}
                  </h3>
                  <p style={{
                    fontSize: isMobile ? '14px' : '15px',
                    color: '#6c757d',
                    margin: '0 0 4px 0'
                  }}>
                    📅 {formatearFecha(partido.jornada.fecha)}
                  </p>
                  <p style={{
                    fontSize: isMobile ? '14px' : '15px',
                    color: '#6c757d',
                    margin: '0 0 4px 0'
                  }}>
                    🕐 {formatearHora(partido.hora || partido.jornada.hora)}
                  </p>
                  <p style={{
                    fontSize: isMobile ? '14px' : '15px',
                    color: '#6c757d',
                    margin: '0 0 8px 0'
                  }}>
                    📍 {partido.sede.nombre} - {partido.sede.poblacion}
                  </p>
                  {partido.miEquipo && (
                    <p style={{
                      fontSize: isMobile ? '13px' : '14px',
                      color: '#4352A9',
                      margin: 0,
                      fontWeight: '600'
                    }}>
                      🏃‍♂️ Mi equipo: {partido.miEquipo.nombre}
                    </p>
                  )}
                </div>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: getEstadoColor(partido.estado),
                  color: '#ffffff'
                }}>
                  {partido.estadoTexto}
                </span>
              </div>
              
              {/* Mostrar resumen de ProPoints para partidos finalizados */}
              {partido.estado === 'finalizado' && logrosPartidos[partido.id] && (
                <div style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  border: '1px solid #e1e5e9'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#061156',
                      margin: 0
                    }}>
                      🏆 ProPoints Asignados
                    </h4>
                    {(() => {
                      const resumen = getResumenLogros(partido.id);
                      return resumen && (
                        <span style={{
                          fontSize: '12px',
                          color: '#6c757d',
                          fontWeight: '500'
                        }}>
                          {resumen.totalJugadores} jugadores • {resumen.totalLogros} logros • {resumen.totalPuntos} pts
                        </span>
                      );
                    })()}
                  </div>

                  {/* Mostrar algunos logros destacados */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    maxHeight: '60px',
                    overflow: 'hidden'
                  }}>
                    {Object.entries(logrosPartidos[partido.id]).slice(0, 3).map(([futbolistaId, jugadorLogros]) =>
                      jugadorLogros.slice(0, 2).map((logro, index) => (
                        <span
                          key={`${futbolistaId}-${index}`}
                          style={getBadgeStyle(logro.logro_nombre)}
                        >
                          {logro.logro_nombre} +{logro.puntos}
                        </span>
                      ))
                    )}
                    {Object.keys(logrosPartidos[partido.id]).length > 3 && (
                      <span style={{
                        ...getBadgeStyle(''),
                        backgroundColor: '#6c757d',
                        color: 'white'
                      }}>
                        +más...
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <button
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '1px solid #4352A9',
                    backgroundColor: '#ffffff',
                    color: '#4352A9',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => {
                    if (partido.estado === 'finalizado') {
                      navigate(`/coach/partidos/${partido.id}/propoints`);
                    } else {
                      navigate(`/coach/partidos/${partido.id}`);
                    }
                  }}
                >
                  {partido.estado === 'finalizado' ? 'Ver ProPoints' : 'Ver Detalles'}
                </button>

                {partido.estado === 'confirmado' && (
                  <button
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#4352A9',
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => {
                      navigate(`/coach/partidos/${partido.id}/propoints`);
                    }}
                  >
                    Asignar ProPoints
                  </button>
                )}

                {/* Botón de notificación solo si el partido está finalizado y tiene logros */}
                {partido.estado === 'finalizado' && tieneLogros(partido.id) && (
                  <button
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: partidosNotificados[partido.id]
                        ? '#6c757d'
                        : (enviandoNotificacion[partido.id] ? '#6c757d' : '#28a745'),
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: (partidosNotificados[partido.id] || enviandoNotificacion[partido.id])
                        ? 'not-allowed'
                        : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: (partidosNotificados[partido.id] || enviandoNotificacion[partido.id])
                        ? 0.7
                        : 1
                    }}
                    onClick={() => !partidosNotificados[partido.id] && handleNotificarProPoints(partido.id)}
                    disabled={partidosNotificados[partido.id] || enviandoNotificacion[partido.id]}
                  >
                    {partidosNotificados[partido.id]
                      ? '✅ Ya Notificado'
                      : (enviandoNotificacion[partido.id] ? '⏳ Enviando...' : '🔔 Notificar ProPoints')
                    }
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default CoachPartidos;
